/**
 * Graph construction algorithm for the IVR Journey Visualisation.
 * Traverses configuration relationships from a DNIS entry point outward,
 * building React Flow nodes and edges, then applies Dagre layout.
 *
 * BR-14: REQ-3 — Automatic Graph Construction
 */

import dagre from '@dagrejs/dagre';
import { Node, Edge, Position } from 'reactflow';
import { ConfigurationObject } from '@features/configs/services/configs-api';
import { CONFIG_REFERENCE_FIELDS, NODE_ROLE_MAP, ROLE_COLOURS, getEdgeLabel } from '../components/nodes/nodeConfig';

const MAX_DEPTH = 10;

export interface GraphData {
  nodes: Node[];
  edges: Edge[];
}

export interface JourneyNodeData {
  configId: string;
  configName: string;
  configType: string;
  role: string;
  roleColour: string;
  config: ConfigurationObject;
  childCount?: number;
  isCollapsed?: boolean;
  hasPendingDeletionWarning?: boolean;
}

/**
 * Builds the journey graph by traversing config relationships from a start DNIS.
 *
 * @param startConfigId - The DNIS configId to start traversal from
 * @param allConfigs - All configurations from the portal
 * @returns GraphData with positioned nodes and edges for React Flow
 */
export function buildGraph(startConfigId: string, allConfigs: ConfigurationObject[]): GraphData {
  const configMap = new Map(allConfigs.map((c) => [c.configId, c]));
  // Build a name→configId lookup for fallback resolution (case-insensitive)
  const nameToIdMap = new Map<string, string>();
  for (const c of allConfigs) {
    nameToIdMap.set(c.configName.toLowerCase(), c.configId);
  }

  const visited = new Set<string>();
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  /**
   * Resolves a reference value to a configId.
   * Tries exact configId match first, then falls back to configName match.
   */
  function resolveReference(value: string): string | null {
    if (configMap.has(value)) return value;
    // Fallback: try matching by configName (case-insensitive)
    const byName = nameToIdMap.get(value.toLowerCase());
    if (byName) return byName;
    return null;
  }

  function traverse(configId: string, depth: number): void {
    if (visited.has(configId) || depth > MAX_DEPTH) return;
    visited.add(configId);

    const config = configMap.get(configId);
    if (!config) return;

    const role = NODE_ROLE_MAP[config.configType] ?? 'routing';
    const roleColour = ROLE_COLOURS[role] ?? ROLE_COLOURS.routing;

    // Check if any payload reference points to a pending-deletion config
    const hasPendingDeletionWarning = checkPendingDeletionRefs(config, configMap);

    nodes.push({
      id: configId,
      type: 'ivrNode',
      position: { x: 0, y: 0 }, // Will be set by Dagre
      data: {
        configId,
        configName: config.configName,
        configType: config.configType,
        role,
        roleColour,
        config,
        hasPendingDeletionWarning,
      } as JourneyNodeData,
    });

    // Get reference fields for this config type
    const refFields = CONFIG_REFERENCE_FIELDS[config.configType] ?? [];

    for (const field of refFields) {
      const rawValue = config.payload?.[field] as string | undefined;
      if (!rawValue || typeof rawValue !== 'string') continue;

      // Resolve reference — try configId first, then configName fallback
      const targetId = resolveReference(rawValue);
      if (!targetId) continue;

      const label = getEdgeLabel(field);

      edges.push({
        id: `${configId}-${field}-${targetId}`,
        source: configId,
        target: targetId,
        label: label || undefined,
        type: 'smoothstep',
      });

      traverse(targetId, depth + 1);
    }
  }

  traverse(startConfigId, 0);

  // Count children for collapse toggle
  for (const node of nodes) {
    const childEdges = edges.filter((e) => e.source === node.id);
    (node.data as JourneyNodeData).childCount = childEdges.length;
  }

  // Apply Dagre layout
  return applyDagreLayout(nodes, edges);
}

/**
 * Checks if any reference field in a config points to a pending-deletion config.
 */
function checkPendingDeletionRefs(
  config: ConfigurationObject,
  configMap: Map<string, ConfigurationObject>
): boolean {
  const refFields = CONFIG_REFERENCE_FIELDS[config.configType] ?? [];
  for (const field of refFields) {
    const rawValue = config.payload?.[field] as string | undefined;
    if (!rawValue) continue;
    // Check by configId first
    let target = configMap.get(rawValue);
    // Fallback: check by configName
    if (!target) {
      for (const c of configMap.values()) {
        if (c.configName.toLowerCase() === rawValue.toLowerCase()) {
          target = c;
          break;
        }
      }
    }
    if (target?.deletionScheduledAt) return true;
  }
  return false;
}

/**
 * Applies Dagre left-to-right layout to position nodes.
 */
export function applyDagreLayout(nodes: Node[], edges: Edge[]): GraphData {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'LR',
    ranksep: 80,
    nodesep: 24,
    marginx: 40,
    marginy: 40,
  });

  const nodeWidth = 168;
  const nodeHeight = 56;

  for (const node of nodes) {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  }

  for (const edge of edges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  const positionedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
  });

  return { nodes: positionedNodes, edges };
}
